import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import {
    getProductGridView,
    PRODUCT_GRID_VIEW_DEFAULT,
    PRODUCT_GRID_VIEW_PARAM,
    type ProductGridView,
} from '@/lib/product-grid-view';

function buildSearchWithGridView(search: string, gridView: ProductGridView): string {
    const params = new URLSearchParams(search);

    if (gridView === PRODUCT_GRID_VIEW_DEFAULT) {
        params.delete(PRODUCT_GRID_VIEW_PARAM);
    } else {
        params.set(PRODUCT_GRID_VIEW_PARAM, gridView);
    }

    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
}

export function useProductGridView() {
    const location = useLocation();
    const navigate = useNavigate();
    const [gridView, setGridView] = useState<ProductGridView>(() =>
        getProductGridView(new URLSearchParams(location.search))
    );

    useEffect(() => {
        setGridView(getProductGridView(new URLSearchParams(location.search)));
    }, [location.search]);

    const setProductGridView = useCallback(
        (nextGridView: ProductGridView) => {
            setGridView(nextGridView);
            void navigate(
                {
                    pathname: location.pathname,
                    search: buildSearchWithGridView(location.search, nextGridView),
                },
                { replace: true, preventScrollReset: true }
            );
        },
        [location.pathname, location.search, navigate]
    );

    return [gridView, setProductGridView] as const;
}

export { PRODUCT_GRID_VIEW_DEFAULT, PRODUCT_GRID_VIEW_LARGE } from '@/lib/product-grid-view';
